import type React from 'react';

import type { CellContext, HeaderContext } from '@tanstack/react-table';
import type { HandlerRecord } from '@vlabs/api/artemy/model';
import type { TBuildTableViewCell } from '@vlabs/monokit/shared/ui/table-view';
import { buildTableViewCell } from '@vlabs/monokit/shared/ui/table-view';

type TSharedCtx = CellContext<HandlerRecord, unknown> | HeaderContext<HandlerRecord, unknown>;

const dataTestIdFromContext = (
  context: TSharedCtx,
): { 'data-testid': string } => {
  if ((context as CellContext<HandlerRecord, unknown>).row) {
    const cellContext = context as CellContext<HandlerRecord, unknown>;
    return {
      'data-testid': `${context.column.id}.cell.${cellContext.row.original.handler_id}`,
    };
  }

  return { 'data-testid': `${context.column.id}.header` };
};

export function renderHeader(
  renderer: (context: TSharedCtx) => TBuildTableViewCell,
  value: React.ReactNode = '',
) {
  return (context: HeaderContext<HandlerRecord, unknown>): React.JSX.Element => (
    renderer(context)(value)
  );
}

export function renderCell<TValue extends React.ReactNode>(
  renderer: (context: TSharedCtx) => TBuildTableViewCell,
  wrap?: (context: CellContext<HandlerRecord, TValue>) => React.ReactNode,
) {
  return (context: CellContext<HandlerRecord, unknown>): React.JSX.Element => {
    const typedContext = context as CellContext<HandlerRecord, TValue>;

    return renderer(context)(
      wrap ? wrap(typedContext) : typedContext.getValue(),
    );
  };
}

export const descriptionCell = (
  context: TSharedCtx,
): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '1 1 320px',
    maxWidth: '320px',
    minWidth: '320px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  ...dataTestIdFromContext(context),
});

export const handlerIdCell = (
  context: TSharedCtx,
): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '0 0 320px',
    maxWidth: '320px',
    minWidth: '320px',
  },
  ...dataTestIdFromContext(context),
});

export const ownerDepartmentCell = (
  context: TSharedCtx,
): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '0 0 320px',
    marginRight: 'auto',
    maxWidth: '320px',
    minWidth: '320px',
  },
  ...dataTestIdFromContext(context),
});

export const actionCell = (
  context: TSharedCtx,
): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '0 0 40px',
    padding: 0,
  },
  ...dataTestIdFromContext(context),
});

export const dividerCell = buildTableViewCell({
  style: {
    flex: '0 0 16px',
    padding: 0,
  },
});
